import { type ChangeEvent, type FC, useMemo, useRef, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { Presentation } from "@clientFrontend/features/entry/components/NotAdopted/Presentation";
import { csrfToken } from "@root/shared/utils/csrf";
import { type Props, type FormValues } from "./type";

export const NotAdopted: FC<Props> = ({
  notAdoptedContent,
  mailBodyParts,
  updateNotAdoptedPath,
  returnPagePath,
  recruitmentFeePath,
  gaLabel,
  isSp,
  isApp,
}) => {
  const [completedInterview, setCompletedInterview] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [notAdoptedReasons, setNotAdoptedReasons] = useState<string[][]>([]);
  const [notAdoptedDescriptionTextLength, setNotAdoptedDescriptionLength] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const methods = useForm<FormValues>({
    defaultValues: {
      not_adopted_reason: "",
      not_adopted_description: "",
      message: "",
    },
  });

  const formEl = useRef<HTMLFormElement>(null);

  // NOTE: 採用管理アプリ側で不採用報告する場合
  //       /client/clients/{client_id}/job_entries/{job_entry_id}/update_not_adoptedへの遷移を検知
  //       →アラートを出す→OKを押下したら応募詳細へ遷移
  //       という仕様になっている。非同期通信の場合、アプリ側で
  //       /client/clients/{client_id}/job_entries/{job_entry_id}/update_not_adoptedへの遷移を検知できず
  //       画面が固まってしまうため同期通信を選択した
  const onSubmit: SubmitHandler<FormValues> = () => {
    setIsLoading(true);
    formEl.current?.submit();
  };

  const handleRadioButtonChange = (value: string) => {
    const key = "completed_interview";
    methods.clearErrors(key);
    setCompletedInterview(value);
    methods.setValue(key, value);
    // 「面接・見学」項目の選択によって「不採用理由」の選択肢が変わるため変更があるごとに値をリセット。
    // バリデーションエラー時のerrorは残しておきたいので keepError: true にする。
    methods.resetField("not_adopted_reason", { keepError: true });
    if (value === "true") {
      setNotAdoptedReasons(notAdoptedContent.notAdoptedReasonsForInterviewed);
    } else {
      setNotAdoptedReasons(notAdoptedContent.notAdoptedReasonsForNotInterviewed);
    }
  };

  const handleChangeCheckbox = () => {
    methods.clearErrors("note");
    setIsChecked(!isChecked);
    methods.setValue("note", !isChecked);
  };

  const handleTextAreaChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    methods.clearErrors("not_adopted_description");
    setNotAdoptedDescriptionLength(e.target.value.length);
  };

  const authenticityToken = useMemo(() => csrfToken(), []);

  return (
    <Presentation
      userName={notAdoptedContent.userName}
      isPayPerJobEntryFeeType={notAdoptedContent.isPayPerJobEntryFeeType}
      notAdoptedReasons={notAdoptedReasons}
      mailChoices={notAdoptedContent.mailChoices}
      mailDetail={notAdoptedContent.mailDetail}
      methods={methods}
      completedInterview={completedInterview}
      mailBodyParts={mailBodyParts}
      handleRadioButtonChange={handleRadioButtonChange}
      isChecked={isChecked}
      handleChangeCheckbox={handleChangeCheckbox}
      notAdoptedDescriptionTextLength={notAdoptedDescriptionTextLength}
      handleTextAreaChange={handleTextAreaChange}
      onSubmit={onSubmit}
      returnPagePath={returnPagePath}
      recruitmentFeePath={recruitmentFeePath}
      gaLabel={gaLabel}
      isSp={isSp}
      isApp={isApp}
      formEl={formEl}
      updateNotAdoptedPath={updateNotAdoptedPath}
      token={authenticityToken}
      isLoading={isLoading}
    />
  );
};
